/**
 * This component maps chart names to actual chart components
 */

import RA from 'ra-config'
import result from 'lodash/result'
import * as parse from '../../../parse'
import ValuationBoxWhisker from '../../charts/valuation-box-whisker'
import ScatterPlot from '../../charts/scatterplot'
import LineChart from '../../charts/line-chart'
import PerformanceHistory from '../../charts/performance-history'
import ModelHistory from '../../charts/model-history'
import Matrix from '../../charts/factor-correlation-matrix'
import { formatFor } from '../../../format'
import { FactorLabels } from '../../../config/attribute-labels'
import { scaleLog } from 'd3-scale'

const ChartConfig = RA.app.factors.chartConfig

function findOrDefault(list, predicate) {
  let item = list.find(predicate)

  return item || list.find(o => o.default)
}

export default {
  Factors: function({ query, aggregate }) {
    const {
      horizontalAxisLabels,
      horizontalOptionList,
      verticalAxisLabels,
      verticalOptionList
    } = ChartConfig.scatterPlot

    const xAxisItem = findOrDefault(
      horizontalOptionList,
      o => o.value === query.xAxis
    )
    const yAxisItem = findOrDefault(
      verticalOptionList,
      o => o.prefix + o.suffix === query.yAxis
    )

    const yAttr = yAxisItem.prefix + yAxisItem.suffix
    const xAttr = yAxisItem.prefix + xAxisItem.value

    const labelX = horizontalAxisLabels[yAxisItem.prefix][xAxisItem.value]
    const labelY = verticalAxisLabels[yAttr]

    return {
      component: ScatterPlot,
      labelX: labelX,
      labelY: labelY,
      data: parse.scatterplot(aggregate, xAttr, yAttr),
      formatX: formatFor(xAttr),
      formatY: formatFor(yAttr),
      hasLinkedDots: true
    }
  },

  'Aggregate Valuation': function({ currentAggregate, query }) {
    return {
      component: ValuationBoxWhisker,
      data: parse.valuationByType(currentAggregate, 'Agg'),
      formatY: formatFor('CurrentRelValAgg'),
      injectY: [0.0625, 8],
      ticksY: 5
    }
  },

  'P/B Valuation': function({ currentAggregate, query }) {
    return {
      component: ValuationBoxWhisker,
      data: parse.valuationByType(currentAggregate, 'PB'),
      formatY: formatFor('CurrentRelValPB'),
      injectY: [0.0625, 8],
      ticksY: 5
    }
  },

  'Performance History Aggregate': function({ series }) {
    return {
      component: LineChart,
      scaleY: scaleLog().base(2),
      datasets: parse.performanceHistory(
        series,
        { withValuation: false },
        'WealthGrowth'
      ),
      formatY: formatFor('WealthGrowth'),
      // Intentional. We do not want a horizontal axis
      labelX: '',
      labelY: FactorLabels.WealthGrowth
    }
  },

  Model: function({ series }) {
    let trendline = null
    let marker = null
    let median = null
    let formatX = formatFor('RelValAgg')

    if (series.length) {
      let {
        ModelFitAlpha,
        ModelFitBeta,
        CurrentRelValAgg,
        RelValAgg50th
      } = series[0]

      /**
       * y = a + b * ln(x)
       */
      trendline = x => ModelFitAlpha + ModelFitBeta * Math.log(x)

      // Represents the current valuation and current expected return
      marker = {
        x: CurrentRelValAgg,
        y: trendline(CurrentRelValAgg),
        title: `Current`
      }

      median = RelValAgg50th
    }

    return {
      component: ScatterPlot,
      damping: 26,
      data: parse.model(series),
      labelX: FactorLabels.RelValAgg,
      labelY: FactorLabels.Subsequent5YR,
      formatY: formatFor('Subsequent5YR'),
      formatX: formatX,
      trendline: trendline,
      marker: marker,
      median: median
    }
  },

  'Model History': function({ series, expanded }) {
    return {
      component: ModelHistory,
      datasets: parse.modelHistory(series),
      labelX: 'Year',
      labelY: FactorLabels.Exp5YRExReturn,
      formatY: formatFor('Subsequent5YR'),
      labelLine1: FactorLabels.Exp5YRExReturnLine,
      labelLine2: FactorLabels.Subsequent5YRLine,
      expanded: expanded
    }
  },

  'Performance History': function({ series, expanded }) {
    let primaryColor = null,
      domainY1 = null,
      domainY2 = null

    if (series.length) {
      primaryColor = `${series[0].Color}`
      let domains = result(ChartConfig.performanceHistoryDomains, [
        `${series[0].Region}`,
        `${series[0].Category}`,
        `${series[0].Name}`
      ])

      if (domains && domains.y1) {
        domainY1 = domains.y1
      }

      if (domains && domains.y2) {
        domainY2 = domains.y2
      }
    }

    return {
      component: PerformanceHistory,
      datasets: parse.performanceHistory(
        series,
        { withValuation: true },
        'WealthGrowth'
      ),
      labelX: 'Year',
      labelY1: FactorLabels.WealthGrowth,
      labelY2: FactorLabels.RelValAgg,
      formatY1: formatFor('WealthGrowth'),
      formatY2: formatFor('WealthGrowth'), // the same, so that axis match
      domainY1: domainY1,
      domainY2: domainY2,
      primaryColor: primaryColor,
      expanded: expanded
    }
  },

  'Historical Correlation (Large)': function({
    currentAggregate,
    factorCorrelations,
    query,
    expanded
  }) {
    return {
      component: Matrix,
      data: parse.correlation(currentAggregate, factorCorrelations, {
        expanded: expanded,
        filter: 'Large',
        category: query.category,
        selected: query.selected
      })
    }
  },

  'Historical Correlation (Small)': function({
    currentAggregate,
    factorCorrelations,
    query,
    expanded
  }) {
    return {
      component: Matrix,
      data: parse.correlation(currentAggregate, factorCorrelations, {
        expanded: expanded,
        filter: 'Small',
        category: query.category,
        selected: query.selected
      })
    }
  }
}



// WEBPACK FOOTER //
// ./src/components/factors/charts/chart-map.js